'use client'

import { useContext } from 'react'
import { useRouter } from 'next/navigation'
import { useTranslation } from 'react-i18next'
import { CreateContext } from '../contexts/CreateContext'
import { safeSetItem } from '../helpers/safeStorage'
import {
  videoSettingsToClone,
  videoVersionFromDoc,
} from '../helpers/videoVersions'

// Creative "use this image" actions for the fullscreen ImmersiveFeed's ellipsis
// menu + primary Clone/Extend CTA. Mirrors the handlers in Card.jsx (Clone /
// Extend / Animate / Use Character / Chat / Tweak) so the viewer behaves exactly
// like the grid card's overlay. Kept separate (rather than refactoring the
// heavily-used Card) to avoid any risk to the grid; keep the two in sync.

const edit = (url) =>
  (url || '').replace('bucket.promptchan.ai', 'bucket.aicloudnetservices.com')

const modelMap = {
  Real: 'Real',
  Hyperreal: 'Hyperreal',
  Anime: 'Anime',
  Hyperanime: 'Anime',
  'K-Pop': 'Real',
  Fur: 'Hyperreal',
}

export default function useImageActions(imageData) {
  const router = useRouter()
  const { i18n } = useTranslation()
  const {
    setCharImage,
    registerCharImageId,
    setVideoRefImage,
    setVideoRefId,
    setVideoRefLora,
    setFormatTab,
    setVideoStartingPoint,
  } = useContext(CreateContext)

  const id = imageData?.id
  const editedImageUrl = edit(imageData?.imageUrl)

  const getPrompt = () => {
    switch (i18n.language) {
      case 'ja':
        return imageData?.prompt_ja || imageData?.description
      case 'de':
        return imageData?.prompt_de || imageData?.description
      case 'zh':
      case 'zh-CN': // the code the site runs under (supported-locales.js)
        return imageData?.prompt_zh || imageData?.description
      case 'es':
        return imageData?.prompt_es || imageData?.description
      default:
        return imageData?.description
    }
  }

  const getNegativePrompt = () => {
    switch (i18n.language) {
      case 'ja':
        return imageData?.negative_prompt_ja || imageData?.negativePrompt || ''
      case 'de':
        return imageData?.negative_prompt_de || imageData?.negativePrompt || ''
      case 'zh':
      case 'zh-CN': // the code the site runs under (supported-locales.js)
        return imageData?.negative_prompt_zh || imageData?.negativePrompt || ''
      case 'es':
        return imageData?.negative_prompt_es || imageData?.negativePrompt || ''
      default:
        return imageData?.negative_prompt || imageData?.negativePrompt || ''
    }
  }

  const handleClone = () => {
    if (typeof window === 'undefined') return
    const prompt = getPrompt()
    const negativePrompt = getNegativePrompt()

    if (imageData?.video) {
      setVideoRefImage(null)
      setVideoRefId(null)
      setVideoStartingPoint('prompt')
      safeSetItem(
        'cloneData',
        JSON.stringify({
          videoModel: imageData.model,
          videoImageSize: `${imageData.width}x${imageData.height}`,
          videoPrompt: prompt,
          videoNegativePrompt: negativePrompt,
          videoLora: imageData.loras ?? imageData.loras,
          seed: imageData.seed,
          cameraMotion: imageData.camera_motion || 'still',
          formatTab: 'video',
          // Carried none of the voice settings, so cloning a v4-voice clip in the fullscreen
          // feed landed on the voice model with the spoken line CLEARED (Create sets dialogue
          // to '' when the clone data has none) — a silent render on the model whose whole
          // point is speech.
          ...videoSettingsToClone(imageData),
          age_slider_video: imageData.age_slider ?? null,
          weight_slider_video: imageData.weight_slider ?? null,
          breast_slider_video: imageData.breast_slider ?? null,
          ass_slider_video: imageData.ass_slider ?? null,
          loop: imageData.loop ? 'Loop' : 'Repeat',
          videoFramerateV2: imageData.frame_rate ?? 25,
          videoQuality: imageData.quality ?? 'Standard',
        }),
      )
    } else {
      safeSetItem(
        'cloneData',
        JSON.stringify({
          model: imageData.model,
          imageSize: `${imageData.width}x${imageData.height}`,
          creativity: imageData.creativity ? imageData.creativity : 50,
          prompt,
          negativePrompt,
          loras: imageData.loras ? imageData.loras : 'Default',
          lorasList: imageData.lorasList ? imageData.lorasList : [],
          seed: imageData.seed,
          filter: imageData.filter ? imageData.filter : 'Default',
          details: imageData.details ? imageData.details : 0,
          emotion: imageData.expression ? imageData.expression : 'Default',
          formatTab: 'photo',
          age_slider: imageData.age_slider ?? null,
          weight_slider: imageData.weight_slider ?? 0,
          breast_slider: imageData.breast_slider ?? 0,
          ass_slider: imageData.ass_slider ?? 0,
          aspect: imageData?.aspect,
        }),
      )
    }

    router.push('/generate')
  }

  // Tweak (Qwen-Edit) hand-off: /generate reads `tweakData` on mount. Images only.
  const handleTweak = () => {
    safeSetItem(
      'tweakData',
      JSON.stringify({
        imageId: id,
        imageUrl: imageData?.original_image_url ?? editedImageUrl,
      }),
    )
    router.push('/generate')
  }

  const handleExtend = () => {
    if (typeof window === 'undefined') return
    let cloneData = {}
    try {
      const stored = localStorage.getItem('cloneData')
      if (stored) cloneData = JSON.parse(stored)
    } catch {
      /* corrupt cloneData — start fresh rather than throw */
    }

    safeSetItem(
      'cloneData',
      JSON.stringify({
        ...cloneData,
        formatTab: 'video',
        videoModel: modelMap[imageData.model] ?? imageData.model,
      }),
    )
    safeSetItem('extendVideoId', imageData.id)
    // The extend modal offers a spoken line only for v5 sources, and defaults to v4 when this is
    // absent — so without it, extending a v4-voice clip from the feed could not speak at all.
    safeSetItem('extendVideoVersion', videoVersionFromDoc(imageData))

    router.push('/generate')
  }

  const useAnimate = async () => {
    if (typeof window === 'undefined') return
    try {
      const prompt = getPrompt()
      const negativePrompt = getNegativePrompt()

      setVideoRefImage(imageData.original_image_url ?? editedImageUrl)
      setVideoRefId(imageData?.id)
      setVideoRefLora(imageData.loras)
      setVideoStartingPoint('image')

      const storedCloneData = localStorage.getItem('cloneData')
      let cloneData = storedCloneData ? JSON.parse(storedCloneData) : {}

      const mappedModel = modelMap.hasOwnProperty(imageData.model)
        ? modelMap[imageData.model]
        : imageData.model

      cloneData = {
        ...cloneData,
        videoPrompt: prompt,
        videoNegativePrompt: negativePrompt,
        videoModel: mappedModel,
        videoLora: 'Default',
        formatTab: 'video',
        videoImageSize: `${imageData.width}x${imageData.height}`,
        videoId: id,
        autoAnimate: true,
      }

      localStorage.setItem('cloneData', JSON.stringify(cloneData))
      router.push('/generate')
    } catch {
      /* non-fatal */
    }
  }

  const useCharacter = async () => {
    try {
      const charUrl = imageData.original_image_url ?? editedImageUrl
      setCharImage(charUrl)
      registerCharImageId(
        charUrl,
        imageData.original_image_url ? imageData.original_url : imageData.id,
      )
      setFormatTab('photo')
      router.push('/generate')
    } catch {
      /* non-fatal */
    }
  }

  const useChat = async () => {
    if (typeof window === 'undefined') return
    try {
      localStorage.setItem(
        'chatImage',
        imageData?.original_image_url ?? editedImageUrl,
      )
      localStorage.setItem('chatId', imageData?.id)
      localStorage.setItem('chatImageModel', imageData?.model || '')
      router.push('/chat')
    } catch (error) {
      alert(error.message)
    }
  }

  // Start a generation preset to this model (mirrors the grid card's model badge).
  const handleModel = () => {
    if (typeof window === 'undefined') return
    safeSetItem('cloneData', JSON.stringify({ model: imageData?.model }))
    router.push('/generate')
  }

  return {
    handleClone,
    handleExtend,
    useAnimate,
    useCharacter,
    useChat,
    handleTweak,
    handleModel,
  }
}
